import { useCallback } from 'react';
import { BASE_PATH } from '../domain/routes';
import { useUrlState } from './useUrlState.hook';
import { useUrlNavigation } from './useUrlNavigation.hook';
import { useBodyScroll } from './useBodyScroll.hook';

/**
 * Hook for MFE-aware navigation using step/benefit query params and URL path detection.
 *
 * @returns {Object} Navigation utilities and state for MFE.
 *
 * @example
 * const { navigateToNestedPath, clearNestedPath } = useMFENavigation();
 * navigateToNestedPath('details');
 */
export function useMFENavigation() {
  const { pathname, searchParams, isMFE, parentPath, nestedPath, nestedModalPath } = useUrlState();
  const { buildUrl, navigate } = useUrlNavigation();

  // Apply body scroll effect
  useBodyScroll(!!nestedPath);

  const clearNestedPath = useCallback(() => {
    if (isMFE) {
      const params = new URLSearchParams(searchParams.toString());
      params.delete('step');
      params.delete('benefit');
      navigate(buildUrl(`${pathname.split('?')[0]}`, params), true);
    } else {
      navigate(buildUrl(`${BASE_PATH}${parentPath ? `${parentPath}` : ''}`, searchParams), true);
    }
  }, [isMFE, pathname, searchParams, parentPath, buildUrl, navigate]);

  const navigateToNestedPath = useCallback(
    (newNestedPath: string, replace = false) => {
      if (isMFE) {
        const params = new URLSearchParams(searchParams);
        params.set('step', newNestedPath);
        navigate(buildUrl(pathname.split('?')[0], params), replace);
      } else {
        const path = `${BASE_PATH}${parentPath ? `${parentPath}` : ''}/${newNestedPath}`;
        navigate(buildUrl(path, searchParams), replace);
      }
    },
    [isMFE, searchParams, pathname, parentPath, buildUrl, navigate],
  );

  const navigateToNestedModalPath = useCallback(
    (newModalPath: string, replace = false) => {
      if (isMFE) {
        const params = new URLSearchParams(searchParams.toString());
        params.set('step', newModalPath);
        navigate(buildUrl(pathname.split('?')[0], params), replace);
      } else {
        const path = `${BASE_PATH}${parentPath}/${nestedPath}/${newModalPath}`;
        navigate(buildUrl(path, searchParams), replace);
      }
    },
    [isMFE, searchParams, pathname, parentPath, nestedPath, buildUrl, navigate],
  );

  const updatePath = useCallback(
    (newNestedPath: string, paramToRemove: string | null = null, replace: boolean = true) => {
      const params = new URLSearchParams(searchParams.toString());
      if (paramToRemove) params.delete(paramToRemove);
      const basePath = `${BASE_PATH}${parentPath}/${newNestedPath}`;
      navigate(buildUrl(basePath, params), replace);
    },
    [parentPath, searchParams, buildUrl, navigate],
  );

  const navigateToBaseUrlPath = useCallback(
    (baseUrl: string = BASE_PATH, replace: boolean = true) => {
      const newPath = pathname.replace(BASE_PATH, baseUrl);
      navigate(buildUrl(newPath, searchParams), replace);
    },
    [pathname, searchParams, buildUrl, navigate],
  );

  return {
    parentPath,
    nestedPath,
    nestedModalPath,
    clearNestedPath,
    navigateToNestedPath,
    navigateToNestedModalPath,
    navigateToBaseUrlPath,
    updatePath,
    queryParams: searchParams,
  };
}
